import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice } from '../MissionShell/MissionShell';
import { InnControlSummary, runInnControl } from '../../api/batch5Api';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload the Management Accounts workbook (.xlsx).',
  'Click Run InnControl Processor. It can take a while for large workbooks.',
  'The processed workbook downloads automatically.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

function InnControlPanel({ mission, onBack }: PanelProps) {
  const [files, setFiles] = useState<File[]>([]);
  const { status, error, result, fail, run } = useFileRun<FileResult<InnControlSummary>>();

  const handleSubmit = async () => {
    if (files.length === 0) {
      fail('Upload the Management Accounts workbook first.');
      return;
    }
    await run(() => runInnControl(files[0]), 'Processing failed.');
  };

  const s = result?.summary;

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS}>
      <div className="mb-5 rounded-lg border border-border bg-muted p-4">
        <p className="mb-2 text-sm font-semibold text-foreground">📝 What it does</p>
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>Splits Account Transactions into per-account sheets.</li>
          <li>Applies the mapped Format 1-10 summary rules.</li>
          <li>Builds the Fixed Asset Register with monthly depreciation.</li>
          <li>Links up a live-formula Balance Sheet.</li>
        </ul>
      </div>

      <FileDrop
        label="Management Accounts workbook"
        required
        accept={mission.accept}
        hint={mission.maxSizeLabel}
        accentColor={mission.color}
        files={files}
        onFiles={setFiles}
      />

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Processing…' : 'Run InnControl Processor'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Processing…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && (
        <div className="mt-4 space-y-3 text-sm">
          <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">✅ {result.filename} downloaded.</div>
          {s?.warnings && (
            <details className="rounded-lg bg-amber-50 px-4 py-3 text-amber-800">
              <summary className="cursor-pointer">Warnings</summary>
              <pre className="mt-2 whitespace-pre-wrap text-xs">{s.warnings}</pre>
            </details>
          )}
          {s?.log && (
            <details className="rounded-lg border border-border bg-muted px-4 py-3 text-muted-foreground">
              <summary className="cursor-pointer text-foreground">Processing logs</summary>
              <pre className="mt-2 whitespace-pre-wrap text-xs">{s.log}</pre>
            </details>
          )}
        </div>
      )}
      {status === 'idle' && <InfoNotice>Upload an Excel file to run the InnControl processor.</InfoNotice>}
    </MissionShell>
  );
}

export default InnControlPanel;
